import React, { useEffect } from 'react';
import { ActionIcon, Button, SegmentedControl } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faGift, faListCheck, faPercent, faPlus } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import { useEmployeeSession } from '../../contexts/EmployeeSessionProvider';
import { MonitoringFilters } from './components/MonitoringFilters';
import { MonitoringTable } from './components/MonitoringTable';
import { OrdersMonitoringFilters } from './components/OrdersMonitoringFilters';
import { OrdersMonitoringTable } from './components/OrdersMonitoringTable';
import { OrderMonitoringDetailModal } from './components/OrderMonitoringDetailModal';
import { PaymentlineAuditModal } from './components/PaymentlineAuditModal';
import { KitchenPrintsTable } from './components/KitchenPrintsTable';
import { KitchenPrintsFilters } from './components/KitchenPrintsFilters';
import { SessionsTable } from './components/SessionsTable';
import { SessionsFilters } from './components/SessionsFilters';
import { KdsAuditTable } from './components/KdsAuditTable';
import { KdsAuditFilters } from './components/KdsAuditFilters';
import { KdsAuditHistoryModal } from './components/KdsAuditHistoryModal';
import { ErrorsTable } from './components/ErrorsTable';
import { ErrorsFilters } from './components/ErrorsFilters';
import { ErrorDetailModal } from './components/ErrorDetailModal';
import { RestigoCallLogsTable } from './components/RestigoCallLogsTable';
import { RestigoCallLogsFilters } from './components/RestigoCallLogsFilters';
import { RestigoCallLogDetailModal } from './components/RestigoCallLogDetailModal';
import { OntopoCallLogsTable } from './components/OntopoCallLogsTable';
import { OntopoCallLogsFilters } from './components/OntopoCallLogsFilters';
import { OntopoCallLogDetailModal } from './components/OntopoCallLogDetailModal';
import { AuditLogsTable } from './components/AuditLogsTable/AuditLogsTable';
import { TransferLogsFilters } from './components/TransferLogsFilters/TransferLogsFilters';
import { MergeFilters } from './components/MergeFilters/MergeFilters';
import { ApprovalRequestsFilters } from './components/ApprovalRequestsFilters';
import { ApprovalRequestsTable } from './components/ApprovalRequestsTable';
import { ZCreditReconcilePanel } from './components/ZCreditReconcilePanel';
import { useMonitoringPage } from './useMonitoringPage';
import { getApprovalActionLabel, getApprovalActionTone, type ApprovalActionTone } from './monitoring-page.constants';
import classes from './monitoring-page.module.scss';

const approvalActionToneClassNames: Record<ApprovalActionTone, string> = {
  globalDiscount: classes.detailKindBadgeGlobalDiscount,
  itemDiscount: classes.detailKindBadgeItemDiscount,
  cancel: classes.detailKindBadgeCancel,
  onTheHouse: classes.detailKindBadgeOnTheHouse,
  generalItem: classes.detailKindBadgeGeneralItem,
};

const approvalActionIcons: Record<ApprovalActionTone, IconProp> = {
  globalDiscount: faPercent as IconProp,
  itemDiscount: faListCheck as IconProp,
  cancel: faBan as IconProp,
  onTheHouse: faGift as IconProp,
  generalItem: faPlus as IconProp,
};

export const MonitoringPage: React.FC = () => {
  const location = useLocation();
  const { selectedTenantId } = useAuth();
  const { employeeState, hydrateEmployeeState } = useEmployeeSession();
  const monitoring = useMonitoringPage();
  const {
    activeSection,
    setActiveSection,
    paymentlines,
    total,
    movementRows,
    movementTotal,
    kitchenPrintRows,
    kitchenPrintTotal,
    sessionRows,
    sessionTotal,
    approvalRequestRows,
    approvalRequestTotal,
    error,
    selectedMovementIndex,
    closeMovementDetail,
    prevMovementDetail,
    nextMovementDetail,
    movementTab,
    setMovementTab,
    mergeRows,
    mergeTotal,
    selectedMergeIndex,
    closeMergeDetail,
    prevMergeDetail,
    nextMergeDetail,
    selectedApprovalRequestIndex,
    selectedApprovalRequestDetail,
    closeApprovalRequestDetail,
    prevApprovalRequestDetail,
    nextApprovalRequestDetail,
    kdsAuditRows,
    kdsAuditTotal,
  } = monitoring;

  const [hydratedSessionToken, setHydratedSessionToken] = React.useState<string | null>(null);

  useEffect(() => {
    if (!selectedTenantId) {
      setHydratedSessionToken(null);
      return;
    }
    const hydrated = hydrateEmployeeState(selectedTenantId);
    setHydratedSessionToken(hydrated?.employeeSession ?? null);
  }, [selectedTenantId, hydrateEmployeeState]);

  const employeeSession =
    employeeState?.employee?.tenantId === selectedTenantId
      ? employeeState.employeeSession
      : hydratedSessionToken;

  useEffect(() => {
    const section = new URLSearchParams(location.search).get('section');
    const nextSection = section === 'payments' || section === 'paymentlines'
      ? 'payments'
      : section === 'kitchenPrints'
      || section === 'orders'
      || section === 'sessions'
      || section === 'mergeLogs'
      || section === 'approvalRequests'
      || section === 'kdsAudit'
      || section === 'zcredit'
      || section === 'errors'
      || section === 'restigo'
      || section === 'ontopo'
      ? section
      : 'orders';
    if (nextSection !== activeSection) {
      setActiveSection(nextSection);
    }
  }, [location.search, activeSection, setActiveSection]);

  const isMovementDetail = activeSection === 'mergeLogs' && movementTab === 'transfers' && selectedMovementIndex !== null;
  const isMergeDetail = activeSection === 'mergeLogs' && movementTab === 'merges' && selectedMergeIndex !== null;
  const isApprovalRequestDetail = activeSection === 'approvalRequests' && selectedApprovalRequestIndex !== null;

  const selectedMovement = isMovementDetail ? movementRows[selectedMovementIndex] : null;
  const movementDetailKind = selectedMovement?.transferKind === 'partial_transfer' ? 'העברה חלקית' : selectedMovement?.transferKind === 'full_transfer' ? 'העברה מלאה' : selectedMovement?.transferKind === 'merge_transfer' ? 'העברה במיזוג' : null;
  const movementDetailSource = selectedMovement?.sourceOrderBefore[0]?.tableName ?? '—';
  const movementDetailTarget = selectedMovement?.targetOrderAfter[0]?.tableName ?? selectedMovement?.targetOrderBefore[0]?.tableName ?? '—';

  const selectedMerge = isMergeDetail ? mergeRows[selectedMergeIndex] : null;
  const mergeDetailKindLabels: Record<string, string> = { merge: 'מיזוג', partial_split: 'פיצול חלקי', full_split: 'פיצול מלא' };
  const mergeDetailKind = selectedMerge ? (mergeDetailKindLabels[selectedMerge.kind] ?? selectedMerge.kind) : null;
  const mergeDetailTables = selectedMerge?.mergedTables.map((t) => t.tableName).join(', ') ?? '—';

  const selectedApprovalRequestAction = selectedApprovalRequestDetail
    ? getApprovalActionLabel(selectedApprovalRequestDetail)
    : 'בקשת אישור';
  const selectedApprovalRequestTone = selectedApprovalRequestDetail
    ? getApprovalActionTone(selectedApprovalRequestDetail)
    : null;

  const subtitle = activeSection === 'payments'
    ? `מציג ${paymentlines.length} שורות מתוך ${total}`
    : activeSection === 'orders'
      ? `מציג ${monitoring.ordersRows.length} שורות מתוך ${monitoring.ordersTotal}`
    : activeSection === 'mergeLogs'
      ? isMovementDetail
        ? `מציג ${selectedMovementIndex + 1} שורות מתוך ${movementRows.length}`
        : isMergeDetail
          ? `מציג ${selectedMergeIndex + 1} שורות מתוך ${mergeRows.length}`
          : movementTab === 'transfers'
            ? `מציג ${movementRows.length} שורות מתוך ${movementTotal}`
            : `מציג ${mergeRows.length} שורות מתוך ${mergeTotal}`
      : activeSection === 'kitchenPrints'
        ? `מציג ${kitchenPrintRows.length} שורות מתוך ${kitchenPrintTotal}`
          : activeSection === 'zcredit'
            ? 'התאמת עסקאות אשראי מול ZCredit'
        : activeSection === 'kdsAudit'
          ? `מציג ${kdsAuditRows.length} שורות מתוך ${kdsAuditTotal}`
        : activeSection === 'errors'
          ? `מציג ${monitoring.errorRows.length} שורות מתוך ${monitoring.errorTotal}`
        : activeSection === 'restigo'
          ? `מציג ${monitoring.restigoRows.length} שורות מתוך ${monitoring.restigoTotal}`
        : activeSection === 'ontopo'
          ? `מציג ${monitoring.ontopoRows.length} שורות מתוך ${monitoring.ontopoTotal}`
          : activeSection === 'sessions'
            ? `מציג ${sessionRows.length} שורות מתוך ${sessionTotal}`
            : `מציג ${approvalRequestRows.length} שורות מתוך ${approvalRequestTotal}`;

  return (
    <div className={classes.container}>
      <div className={classes.card}>
        <div className={classes.toolbar}>
          {isMovementDetail ? (
            <div className={classes.detailToolbar}>
              <button className={classes.backBtn} onClick={closeMovementDetail} type="button" data-testid="monitoring-movement-detail-back">
                {'→ חזרה לרשימה'}
              </button>
              <div className={classes.detailTitleBlock}>
                <span className={classes.detailKindBadge}>{movementDetailKind}</span>
                <span className={classes.detailTitle}>{`${movementDetailSource} ← ${movementDetailTarget}`}</span>
              </div>
              <div className={classes.detailArrows}>
                <button className={classes.arrowBtn} onClick={prevMovementDetail} disabled={selectedMovementIndex === 0} type="button" aria-label="רשומה קודמת" data-testid="monitoring-movement-detail-prev">{'‹'}</button>
                <span className={classes.subtitle}>{subtitle}</span>
                <button className={classes.arrowBtn} onClick={nextMovementDetail} disabled={selectedMovementIndex === movementRows.length - 1} type="button" aria-label="רשומה הבאה" data-testid="monitoring-movement-detail-next">{'›'}</button>
              </div>
            </div>
          ) : isMergeDetail ? (
            <div className={classes.detailToolbar}>
              <button className={classes.backBtn} onClick={closeMergeDetail} type="button" data-testid="monitoring-merge-detail-back">
                {'→ חזרה לרשימה'}
              </button>
              <div className={classes.detailTitleBlock}>
                <span className={classes.detailKindBadge}>{mergeDetailKind}</span>
                <span className={classes.detailTitle}>{mergeDetailTables}</span>
              </div>
              <div className={classes.detailArrows}>
                <button className={classes.arrowBtn} onClick={prevMergeDetail} disabled={selectedMergeIndex === 0} type="button" aria-label="רשומה קודמת" data-testid="monitoring-merge-detail-prev">{'‹'}</button>
                <span className={classes.subtitle}>{subtitle}</span>
                <button className={classes.arrowBtn} onClick={nextMergeDetail} disabled={selectedMergeIndex === mergeRows.length - 1} type="button" aria-label="רשומה הבאה" data-testid="monitoring-merge-detail-next">{'›'}</button>
              </div>
            </div>
          ) : isApprovalRequestDetail ? (
            <div className={classes.detailToolbar}>
              <Button className={classes.backBtn} onClick={closeApprovalRequestDetail} type="button" variant="subtle" size="xs" data-testid="monitoring-approval-detail-back">
                {'→ חזרה לרשימה'}
              </Button>
              <div className={classes.detailTitleBlock}>
                <span
                  className={[
                    classes.detailKindBadge,
                    selectedApprovalRequestTone ? approvalActionToneClassNames[selectedApprovalRequestTone] : '',
                  ].filter(Boolean).join(' ')}
                >
                  {selectedApprovalRequestTone && <FontAwesomeIcon icon={approvalActionIcons[selectedApprovalRequestTone]} />}
                  {selectedApprovalRequestAction}
                </span>
              </div>
              <div className={classes.detailArrows}>
                <ActionIcon className={classes.arrowBtn} onClick={prevApprovalRequestDetail} disabled={selectedApprovalRequestIndex === 0} variant="default" size="sm" aria-label="רשומה קודמת" data-testid="monitoring-approval-detail-prev">{'‹'}</ActionIcon>
                <span className={classes.subtitle}>{`${selectedApprovalRequestIndex + 1} מתוך ${approvalRequestRows.length}`}</span>
                <ActionIcon className={classes.arrowBtn} onClick={nextApprovalRequestDetail} disabled={selectedApprovalRequestIndex === approvalRequestRows.length - 1} variant="default" size="sm" aria-label="רשומה הבאה" data-testid="monitoring-approval-detail-next">{'›'}</ActionIcon>
              </div>
            </div>
          ) : (
            <div className={classes.listToolbar}>
              <div className={classes.titleBlock}>
                <h2 className={classes.title}>{'מעקב'}</h2>
                <p className={classes.subtitle}>{subtitle}</p>
              </div>
              {activeSection === 'mergeLogs' && (
                <SegmentedControl
                  value={movementTab}
                  onChange={(value) => setMovementTab(value === 'merges' ? 'merges' : 'transfers')}
                  data={[
                    { value: 'transfers', label: 'העברות' },
                    { value: 'merges', label: 'מיזוגים ופיצולים' },
                  ]}
                  size="xs"
                  data-testid="monitoring-movement-tab"
                />
              )}
              {activeSection === 'mergeLogs' && movementTab === 'transfers' && (
                <TransferLogsFilters
                  movementFilters={monitoring.movementFilters}
                  setMovementFilters={monitoring.setMovementFilters}
                  clearMovementFilters={monitoring.clearMovementFilters}
                />
              )}
              {activeSection === 'mergeLogs' && movementTab === 'merges' && (
                <MergeFilters
                  mergeFilters={monitoring.mergeFilters}
                  setMergeFilters={monitoring.setMergeFilters}
                  clearMergeFilters={monitoring.clearMergeFilters}
                />
              )}
              {activeSection === 'approvalRequests' && <ApprovalRequestsFilters {...monitoring} />}
            </div>
          )}
        </div>

        {activeSection === 'orders' && <OrdersMonitoringFilters {...monitoring} />}
        {activeSection === 'payments' && <MonitoringFilters {...monitoring} />}
        {activeSection === 'sessions' && <SessionsFilters {...monitoring} />}
        {activeSection === 'kdsAudit' && <KdsAuditFilters {...monitoring} />}
        {activeSection === 'errors' && <ErrorsFilters {...monitoring} />}
        {activeSection === 'restigo' && <RestigoCallLogsFilters {...monitoring} />}
        {activeSection === 'ontopo' && <OntopoCallLogsFilters {...monitoring} />}
        {activeSection === 'kitchenPrints' && (
          <KitchenPrintsFilters
            companyFilter={monitoring.kitchenCompanyFilter}
            setCompanyFilter={monitoring.setKitchenCompanyFilter}
            sessionFilter={monitoring.kitchenSessionFilter}
            setSessionFilter={monitoring.setKitchenSessionFilter}
            orderFilter={monitoring.kitchenOrderFilter}
            setOrderFilter={monitoring.setKitchenOrderFilter}
            companyOptions={monitoring.kitchenCompanyOptions}
            sessionOptions={monitoring.kitchenSessionOptions}
            orderOptions={monitoring.kitchenOrderOptions}
            clearFilters={monitoring.clearKitchenPrintFilters}
          />
        )}

        {error && <p className={classes.error}>{error}</p>}

        {activeSection === 'orders' && <OrdersMonitoringTable {...monitoring} />}
        {activeSection === 'payments' && <MonitoringTable {...monitoring} />}
        {activeSection === 'sessions' && <SessionsTable {...monitoring} />}
        {activeSection === 'kdsAudit' && <KdsAuditTable {...monitoring} />}
        {activeSection === 'errors' && <ErrorsTable {...monitoring} />}
        {activeSection === 'restigo' && <RestigoCallLogsTable {...monitoring} />}
        {activeSection === 'ontopo' && <OntopoCallLogsTable {...monitoring} />}
        {activeSection === 'mergeLogs' && (
          <AuditLogsTable
            movementRows={monitoring.movementRows}
            movementTotalPages={monitoring.movementTotalPages}
            movementPage={monitoring.movementPage}
            setMovementPage={monitoring.setMovementPage}
            movementTab={monitoring.movementTab}
            setMovementTab={monitoring.setMovementTab}
            isMovementLoading={monitoring.isMovementLoading}
            hasLoadedMovements={monitoring.hasLoadedMovements}
            selectedMovementIndex={monitoring.selectedMovementIndex}
            openMovementDetail={monitoring.openMovementDetail}
            prevMovementDetail={monitoring.prevMovementDetail}
            nextMovementDetail={monitoring.nextMovementDetail}
            closeMovementDetail={monitoring.closeMovementDetail}
            mergeRows={monitoring.mergeRows}
            mergeTotalPages={monitoring.mergeTotalPages}
            mergePage={monitoring.mergePage}
            setMergePage={monitoring.setMergePage}
            isMergeLoading={monitoring.isMergeLoading}
            hasLoadedMerges={monitoring.hasLoadedMerges}
            selectedMergeIndex={monitoring.selectedMergeIndex}
            openMergeDetail={monitoring.openMergeDetail}
            prevMergeDetail={monitoring.prevMergeDetail}
            nextMergeDetail={monitoring.nextMergeDetail}
            closeMergeDetail={monitoring.closeMergeDetail}
          />
        )}
        {activeSection === 'approvalRequests' && <ApprovalRequestsTable {...monitoring} />}
        {activeSection === 'zcredit' && (
          <ZCreditReconcilePanel
            tenantId={selectedTenantId}
            employeeSession={employeeSession ?? null}
          />
        )}
        {activeSection === 'kitchenPrints' && (
          <KitchenPrintsTable
            rows={monitoring.kitchenPrintRows}
            isLoading={monitoring.isKitchenPrintLoading}
            hasLoaded={monitoring.hasLoadedKitchenPrints}
            totalPages={monitoring.kitchenPrintTotalPages}
            page={monitoring.kitchenPrintPage}
            setPage={monitoring.setKitchenPrintPage}
          />
        )}
      </div>

      <PaymentlineAuditModal {...monitoring} />
      <OrderMonitoringDetailModal {...monitoring} />
      <KdsAuditHistoryModal {...monitoring} />
      <ErrorDetailModal {...monitoring} />
      <RestigoCallLogDetailModal {...monitoring} />
      <OntopoCallLogDetailModal {...monitoring} />
    </div>
  );
};